import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { BadgeCheck, Building2, CalendarDays, Loader2, ReceiptText, UserRoundX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { EmptyState } from "@/components/common/EmptyState";
import { api, getErrorMessage } from "@/lib/api";
import { formatDate } from "@/lib/constants";

// Phase 35: Admin "Invoice Pending" queue. Records land here automatically the
// moment their locking period ends. Exactly two actions per record:
//   1) Mark Left            — candidate left before payment was settled
//   2) Mark Payment Received — the final, successful outcome

export default function AdminInvoicePending() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [action, setAction] = useState(null); // { mode: 'left' | 'paid', e }
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setError(false);
    api.get("/employment", { params: { status: "Invoice Pending" } })
      .then((res) => setItems(res.data || []))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setBusy(true);
    try {
      const status = action.mode === "left" ? "Left" : "Payment Received";
      const payload = { status };
      if (reason.trim()) payload.reason = reason.trim();
      await api.put(`/employment/${action.e.id}/status`, payload);
      toast.success(action.mode === "left"
        ? `${action.e.candidate?.full_name || "Candidate"} marked Left`
        : `Payment received for ${action.e.candidate?.full_name || "candidate"} — record closed`);
      setAction(null); setReason("");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="space-y-3"><Skeleton className="h-10 w-64 rounded-lg" />{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-14 rounded-xl" />)}</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight">Invoice Pending</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Placements whose locking period has ended — awaiting payment settlement. Two outcomes: Payment Received, or Left.
        </p>
      </div>

      {error ? (
        <Card className="rounded-xl border-border shadow-none p-10 flex flex-col items-center gap-3" data-testid="invoice-pending-error">
          <p className="text-sm text-muted-foreground">Could not load the Invoice Pending queue.</p>
          <Button variant="outline" size="sm" className="rounded-lg bg-white" onClick={load}>Retry</Button>
        </Card>
      ) : items.length === 0 ? (
        <EmptyState icon={ReceiptText} title="No records awaiting payment"
          description="Employments move here automatically the day their locking period ends." />
      ) : (
        <Card className="rounded-xl border-border shadow-none overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>Candidate</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Recruiter</TableHead>
                  <TableHead>Joining</TableHead>
                  <TableHead>Lock ended</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((e) => (
                  <TableRow key={e.id} className="hover:bg-muted/60" data-testid="invoice-pending-row">
                    <TableCell className="font-mono text-xs">
                      <Link to={`/admin/employment/${e.id}`} className="text-primary hover:underline" data-testid={`invoice-pending-link-${e.id}`}>{e.emp_code}</Link>
                    </TableCell>
                    <TableCell>
                      <p className="font-medium text-sm">{e.candidate?.full_name || "—"}</p>
                      <p className="text-xs text-muted-foreground font-mono">{e.candidate?.code}</p>
                    </TableCell>
                    <TableCell className="text-sm"><span className="inline-flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5 text-muted-foreground" />{e.company?.name || "—"}</span></TableCell>
                    <TableCell className="text-sm">{e.recruiter?.name || "—"}</TableCell>
                    <TableCell className="text-sm"><span className="inline-flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5 text-muted-foreground" />{formatDate(e.date_of_joining)}</span></TableCell>
                    <TableCell className="text-sm">{formatDate(e.locking_end_date)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1.5 flex-wrap">
                        <Button size="sm" variant="ghost" className="h-8 rounded-lg text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => { setAction({ mode: "left", e }); setReason(""); }}
                          data-testid={`invoice-pending-left-${e.id}`}>
                          <UserRoundX className="h-3.5 w-3.5 mr-1.5" /> Mark Left
                        </Button>
                        <Button size="sm" className="h-8 rounded-lg"
                          onClick={() => { setAction({ mode: "paid", e }); setReason(""); }}
                          data-testid={`invoice-pending-paid-${e.id}`}>
                          <BadgeCheck className="h-3.5 w-3.5 mr-1.5" /> Payment Received
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      <Dialog open={!!action} onOpenChange={(v) => !v && setAction(null)}>
        <DialogContent className="sm:max-w-md rounded-xl" data-testid="invoice-pending-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {action?.mode === "left" ? "Mark Left" : "Mark Payment Received"} — {action?.e?.candidate?.full_name}
            </DialogTitle>
            <DialogDescription>
              {action?.mode === "left"
                ? `Closes employment ${action?.e?.emp_code} as Left (candidate left before payment was settled). This cannot be undone.`
                : `Final state for employment ${action?.e?.emp_code} — the placement is complete and paid. No further transitions after this.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Note <span className="text-muted-foreground">(optional)</span></Label>
              <Textarea rows={2} className="bg-white" value={reason} placeholder="Recorded in the employment history..."
                onChange={(e) => setReason(e.target.value)} data-testid="invoice-pending-reason-input" />
            </div>
            <Button onClick={submit} disabled={busy}
              variant={action?.mode === "left" ? "destructive" : "default"}
              className="w-full rounded-lg" data-testid="invoice-pending-submit">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {action?.mode === "left" ? "Confirm Mark Left" : "Confirm Payment Received"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
